import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Color from 'color';

import Avatar from 'LegacyWeployDesignLanguage/Avatar/Avatar';
import Dialog from 'WeployDesignLanguage/Dialog';
import TextField from 'LegacyWeployDesignLanguage/inputs/TextField';
import Button from 'WeployDesignLanguage/buttons/Button';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import Table from '../../../components/weployer/common/table';
import formatCurrency from '../../../utils/Currency';

import { rejectTimesheet } from '../../../weployers/Api';

const { Title, Content } = Dialog;

const StyledWeployeeName = styled.div`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
  margin-left: ${props => props.theme.spacing[2]};
`;

const StyledSubmittedAt = styled.div`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
  margin-left: ${props => props.theme.spacing[2]};
`;

const StyledApprovedAt = styled.div`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.success};
  margin-top: ${props => props.theme.spacing[3]};
`;

const StyledHeader = styled.h6`
  margin-top: ${props => props.theme.spacing[2]};
`;

const StyledInvoiceRow = styled(Grid)`
  border-bottom: 1px solid ${props => Color(props.theme.colors.text.primary).fade(0.83).string()};
  color: ${props => props.theme.colors.text.primary};
  padding: ${props => props.theme.spacing[2]};
`;

const StyledFooter = styled.p`
  ${props => props.theme.typography.body1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledAnchor = styled.a`
  font-weight: 600;
`;

const StyledSubtitle = styled.p`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledTablePeriod = styled(StyledSubtitle)`
  margin-left: ${props => props.theme.spacing[2]};
`;

const StyledInvoiceDescription = styled(Grid)`
  ${props => props.theme.typography.body1};
`;

const StyledInvoiceValue = styled(Grid)`
  ${props => props.theme.typography.subtitle1};
`;

const StyledErrorMessage = styled.div`
  color: ${props => props.theme.colors.text.warning};
`;

const StyledRejectButton = styled(Button)`
  margin-top: ${props => props.theme.spacing[2]};
`;

const InvoiceRow = ({ description, amount, currency }) => (
  <StyledInvoiceRow container justify="space-between">
    <StyledInvoiceDescription item>{description}</StyledInvoiceDescription>
    <StyledInvoiceValue item>{formatCurrency(amount, currency)}</StyledInvoiceValue>
  </StyledInvoiceRow>
);

const StyledRejectionReasonModalActionsContainer = styled.div`
  justify-content: flex-end;
  display: flex;
  margin-top: ${props => props.theme.spacing[2]};
`;

const StyledRejectionReasonModalErrorContainer = styled.div`
  text-align: right;
`;

const StyledRejectionTitle = styled.div`
  margin-top: ${props => props.theme.spacing[2]};
`;

const StyledRejectionReason = styled.p`
  margin-top: 0;
  white-space: pre-line;
`;

const StyledRejectionTitleRejected = styled(StyledRejectionTitle)`
  color: ${props => props.theme.colors.text.warning};
`;

const StyledRejectionTitleResolved = styled(StyledRejectionTitle)`
  color: ${props => props.theme.colors.text.primary};
`;

const StyledRejectionReasonRejected = styled(StyledRejectionReason)`
  color: ${props => props.theme.colors.text.warning};
`;

const StyledRejectedReasonResolved = styled(StyledRejectionReason)`
  color: ${props => props.theme.colors.text.secondary};
`;

InvoiceRow.propTypes = {
  description: PropTypes.string.isRequired,
  amount: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
};

const TimesheetSummaryModal = ({
  timesheet: {
    baseHourlyRate,
    ordinaryHours,
    ordinaryWages,
    payrollComplianceFee,
    penaltyHours,
    penaltyWages,
    serviceFee,
    subtotal,
    subtotalGst,
    timesheetEntries,
    weployeeAvatarUrl,
    weployeeName,
    invoicePeriod,
    submittedAtDetails,
    isApproved,
    isRejected,
    isResolved,
    approvedAtDetails,
    currency,
    id,
    rejectionReason: reasonForRejection,
  },
  open,
  onClose,
  onEmailClick,
  setModalOpen,
  onRejectingTimesheet,
  weployerId,
  canRejectFeatureFlag,
}) => {
  const [rejectReasonModalOpen, setRejectReasonModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [reasonMissing, setReasonMissing] = useState(false);
  const [inputDirty, setInputDirty] = useState(false);
  const [timesheetModalErrorMessage, setTimesheetModalErrorMessage] = useState(null);

  const handleRejectionReasonInput = (event) => {
    setRejectionReason(event.target.value);
  };

  const handleCancelRejectionReasonModal = () => {
    setInputDirty(false);
    setReasonMissing(false);
    setRejectionReason('');
    setRejectReasonModalOpen(false);
    setTimesheetModalErrorMessage(null);
  };

  const handleRejectionReasonBlur = () => {
    if (!inputDirty) { setInputDirty(true); }
  };

  const handleKeyDown = (event) => {
    const { value } = event.target;

    if (inputDirty && value === '') {
      setReasonMissing(true);
    } else {
      setReasonMissing(false);
    }
  };

  const handleRejectTimesheet = (timesheetId) => {
    const body = {
      timesheet: {
        id: timesheetId,
        weployer_id: weployerId,
        rejection_reason: rejectionReason.trim(),
      },
    };

    rejectTimesheet(timesheetId, body)
      .then((data) => {
        setModalOpen(false);
        setRejectReasonModalOpen(false);
        setRejectionReason('');
        onRejectingTimesheet({ timesheetId: data.data.id, rejectionReason });
      })
      .catch((error) => {
        console.error(error.message);
        setTimesheetModalErrorMessage(error.message);
      });
  };

  const handleSubmitRejection = () => {
    if (rejectionReason === '') {
      setReasonMissing(true);
      return;
    }

    handleRejectTimesheet(id);
  };

  return (
    <Fragment>
      <Dialog fullScreen onClose={onClose} open={open} width="900px" maxWidth={false}>
        <Title onClose={onClose}>{weployeeName}&apos;s timesheet</Title>
        <Content>
          <Grid container>
            <Grid item>
              <Avatar
                src={weployeeAvatarUrl}
                alt="Weployee's avatar"
                size={Avatar.EXTRA_SMALL}
              />
            </Grid>
            <Grid item>
              <StyledWeployeeName>{weployeeName}</StyledWeployeeName>
              <StyledSubmittedAt>{submittedAtDetails}</StyledSubmittedAt>
            </Grid>
          </Grid>
          {
            isApproved &&
            <StyledApprovedAt>{approvedAtDetails}</StyledApprovedAt>
          }
          { isRejected && canRejectFeatureFlag &&
            <>
              <StyledRejectionTitleRejected><strong>This timesheet was rejected because of these reasons:</strong></StyledRejectionTitleRejected>
              <StyledRejectionReasonRejected>{reasonForRejection}</StyledRejectionReasonRejected>
            </>
          }
          { isResolved && canRejectFeatureFlag &&
            <>
              <StyledRejectionTitleResolved><strong>This timesheet&apos;s rejection was resolved from the reasons below:</strong></StyledRejectionTitleResolved>
              <StyledRejectedReasonResolved>{reasonForRejection}</StyledRejectedReasonResolved>
            </>
          }
          <StyledSubtitle>Invoice period: {invoicePeriod}</StyledSubtitle>
          <h6>Weployee hours worked</h6>
          {
            timesheetEntries.map(({ period, entries }) => (
              <Fragment key={period}>
                <StyledTablePeriod>{period}</StyledTablePeriod>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      <Table.Cell>Date</Table.Cell>
                      <Table.Cell align="right">Total (Hrs)</Table.Cell>
                      <Table.Cell>Start/End Times</Table.Cell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {
                      entries.map(({
                        date,
                        hours,
                        timesheetEntryPeriod,
                      }) => (
                        <Table.Row key={`${date}-${timesheetEntryPeriod}`}>
                          <Table.Cell>{date}</Table.Cell>
                          <Table.Cell align="right">{hours}</Table.Cell>
                          <Table.Cell>{timesheetEntryPeriod}</Table.Cell>
                        </Table.Row>
                      ))
                    }
                  </Table.Body>
                </Table>
              </Fragment>
            ))
          }
          <StyledHeader>Your invoice</StyledHeader>
          <InvoiceRow
            description={`Ordinary Hours x ${ordinaryHours} @ ${formatCurrency(baseHourlyRate, currency)}ph`}
            amount={ordinaryWages}
            currency={currency}
          />
          <InvoiceRow
            description={`Penalty hours x ${penaltyHours}`}
            amount={penaltyWages}
            currency={currency}
          />
          <InvoiceRow
            description="Payroll compliance fee (Super, Payroll Tax, and Work Cover)"
            amount={payrollComplianceFee}
            currency={currency}
          />
          <InvoiceRow
            description="Service fee"
            amount={serviceFee}
            currency={currency}
          />
          <InvoiceRow
            description="GST"
            amount={subtotalGst}
            currency={currency}
          />
          <InvoiceRow
            description="Total"
            amount={subtotal + subtotalGst}
            currency={currency}
          />
          { !isApproved && !canRejectFeatureFlag &&
            <StyledFooter>
              <span>If this timesheet doesn&apos;t look quite right, </span>
              <StyledAnchor
                onClick={onEmailClick}
                href="mailto:hello@weployapp.com?Subject=Timesheet%20query"
                target="_top"
              >
                send an email
              </StyledAnchor>
              <span>, and we&apos;ll get back to you as soon as we can</span>
            </StyledFooter>
          }
          { !isApproved && !isRejected && canRejectFeatureFlag &&
            <Fragment>
              <StyledRejectButton onClick={() => setRejectReasonModalOpen(true)}>Reject</StyledRejectButton>
              <StyledFooter>
                <span>If you have any question, </span>
                <StyledAnchor
                  onClick={onEmailClick}
                  href="mailto:hello@weployapp.com?Subject=Timesheet%20query"
                  target="_top"
                >
                  please send us an email.
                </StyledAnchor>
              </StyledFooter>
            </Fragment>
          }

        </Content>
      </Dialog>
      <Dialog
        open={rejectReasonModalOpen}
        onClose={() => {
          setRejectReasonModalOpen(false);
        }}
      >
        <div>
          <TextField
            error={reasonMissing}
            id="rejectionReason"
            label="Reason"
            placeholder="Please state your reason"
            multiline
            onChange={handleRejectionReasonInput}
            onBlur={handleRejectionReasonBlur}
            onKeyUp={handleKeyDown}
            value={rejectionReason}
          />
          {reasonMissing && <StyledErrorMessage>Reason is required</StyledErrorMessage>}
        </div>
        <StyledRejectionReasonModalActionsContainer>
          <Button appearance={Button.TEXT} onClick={handleCancelRejectionReasonModal}>Cancel</Button>
          <Button onClick={handleSubmitRejection}>Reject timesheet</Button>
        </StyledRejectionReasonModalActionsContainer>
        <StyledRejectionReasonModalErrorContainer>
          <StyledErrorMessage>{timesheetModalErrorMessage}</StyledErrorMessage>
        </StyledRejectionReasonModalErrorContainer>
      </Dialog>
    </Fragment>
  );
};

TimesheetSummaryModal.propTypes = {
  timesheet: PropTypes.object.isRequired,
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  weployerId: PropTypes.string.isRequired,
  onEmailClick: PropTypes.func,
  setModalOpen: PropTypes.func,
  canRejectFeatureFlag: PropTypes.bool,
  onRejectingTimesheet: PropTypes.func,
};

TimesheetSummaryModal.defaultProps = {
  open: false,
  canRejectFeatureFlag: false,
  onEmailClick: () => {},
  setModalOpen: () => {},
  onRejectingTimesheet: () => {},
};

export default TimesheetSummaryModal;
